import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Button, StatusDot } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import {
  useCreateSubscription,
  useDeleteSubscription,
  useEventCatalog,
  useSubscriptions,
  useTestSubscription,
  useAudit,
} from '@/hooks/useNotifications';
import type { Channel, Persona } from '@/types/notification';

import styles from './Notifications.module.css';

const PERSONAS: { value: Persona; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'engenheiro', label: 'Engenheiro' },
  { value: 'reporter', label: 'Repórter' },
  { value: 'social', label: 'Social Media' },
];

const CHANNELS: { value: Channel; label: string }[] = [
  { value: 'in_app', label: 'No painel' },
  { value: 'email', label: 'Email' },
  { value: 'webhook', label: 'Webhook' },
];

export function Notifications() {
  const { t } = useTranslation(['config', 'common']);
  const { data: catalog } = useEventCatalog();
  const { data: subs, refetch } = useSubscriptions();
  const { data: audit } = useAudit({ limit: 30 });
  const createMutation = useCreateSubscription();
  const deleteMutation = useDeleteSubscription();
  const testMutation = useTestSubscription();

  const [name, setName] = useState('');
  const [persona, setPersona] = useState<Persona>('all');
  const [channel, setChannel] = useState<Channel>('in_app');
  const [address, setAddress] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  async function handleCreate() {
    setMsg(null);
    if (!name.trim() || (!address.trim() && channel !== 'in_app') || events.length === 0) {
      setMsg('Preencha nome, endereço (se não in_app) e pelo menos 1 evento.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        persona,
        channel,
        address: address.trim() || 'in-app',
        events,
        active: true,
      });
      setName('');
      setAddress('');
      setEvents([]);
      setMsg('Subscrição criada.');
      refetch();
    } catch (e) {
      setMsg(`Erro: ${String(e)}`);
    }
  }

  async function handleTest(id: string) {
    try {
      await testMutation.mutateAsync({
        id,
        eventId: 'workflow.error',
        message: t('config:notifications.test_message'),
      });
      setMsg('Disparo de teste enviado.');
    } catch (e) {
      setMsg(`Erro no teste: ${String(e)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <Link to="/config" className={styles.back}>
          ← Configurações
        </Link>
        <h1 className={styles.title}>{t('config:notifications.title')}</h1>
      </header>

      {/*
        L4 (docs/ux/analise-de-lacunas.md, seção "Notificações e workers de
        fundo"): a subscrição GRAVA de verdade, mas 6 dos 8 eventos do catálogo
        (backend/app/schemas/notification.py:11-20) não são emitidos por
        nenhuma linha do backend. Os únicos produtores reais são
        budget_alerts._event_for (openai.cost_threshold_80/_100) e core/falhas
        ('worker.falha', que esta tela nem oferece). O aviso fica colado no
        formulário em vez de escondê-lo: o cadastro continua útil e o operador
        sabe o que dele sai hoje.
      */}
      <div className={styles.aviso} role="status">
        <p className={styles.avisoTexto}>
          As subscrições ficam registradas, mas o despachador de notificações ainda não existe: 6
          dos 8 eventos desta lista não são emitidos por nenhuma linha do produto — para eles nenhum
          canal (painel, e-mail, webhook) dispara hoje (em construção).
        </p>
        <p className={styles.avisoDetalhe}>
          Exceção: “Saldo OpenAI atinge 80% do limite” e “Saldo OpenAI esgotado” têm produtor real e
          disparam hoje — por webhook o aviso sai; por e-mail só sai quando o SMTP está no ambiente
          do servidor; “No painel” apenas registra em log, não há caixa de avisos para ler.
        </p>
      </div>

      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Nova subscrição</h2>
        <div className={styles.row}>
          <input
            type="text"
            placeholder="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={styles.input}
          />
          <select
            value={persona}
            onChange={(e) => setPersona(e.target.value as Persona)}
            className={styles.select}
          >
            {PERSONAS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value as Channel)}
            className={styles.select}
          >
            {CHANNELS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <input
          type="text"
          placeholder={
            channel === 'email'
              ? 'email@dominio.com'
              : channel === 'webhook'
                ? 'https://webhook.url/...'
                : 'in-app (sem endereço)'
          }
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className={styles.input}
          disabled={channel === 'in_app'}
        />

        <div className={styles.eventList}>
          <p className={styles.sectionLabel}>Eventos</p>
          {catalog?.map((evt) => (
            <label key={evt.id} className={styles.checkbox}>
              <input
                type="checkbox"
                checked={events.includes(evt.id)}
                onChange={(e) => {
                  setEvents((prev) =>
                    e.target.checked ? [...prev, evt.id] : prev.filter((v) => v !== evt.id),
                  );
                }}
              />
              {evt.label}
            </label>
          ))}
        </div>

        <div className={styles.actions}>
          {msg && <span className={styles.msg}>{msg}</span>}
          <Button onClick={handleCreate} loading={createMutation.isPending}>
            Criar subscrição
          </Button>
        </div>
      </div>

      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Subscrições ativas</h2>
        {subs && subs.length === 0 && <p className={styles.muted}>Nenhuma ainda.</p>}
        <ul className={styles.subList}>
          {subs?.map((s) => (
            <li key={s.id} className={styles.subRow}>
              <div className={styles.subBody}>
                <strong>{s.name}</strong>
                <div className={styles.subMeta}>
                  <Badge size="xs">{s.persona}</Badge>
                  <Badge tone="info" size="xs">
                    {s.channel}
                  </Badge>
                  <span className={styles.muted}>→ {s.address}</span>
                </div>
                <div className={styles.subMeta}>
                  <span className={styles.muted}>
                    eventos: {s.events.length > 0 ? s.events.join(', ') : '—'}
                  </span>
                </div>
              </div>
              <div className={styles.subActions}>
                <Button size="sm" variant="ghost" onClick={() => handleTest(s.id)}>
                  Testar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(s.id)}>
                  ✕
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>{t('config:notifications.recent_audit')}</h2>
        {audit && audit.length === 0 && <p className={styles.muted}>Sem entradas.</p>}
        <ul className={styles.auditList}>
          {audit?.slice(0, 20).map((a) => (
            <li key={a.id} className={styles.auditRow}>
              <StatusDot state={a.action.includes('error') ? 'crit' : 'ok'} />
              <span className={styles.auditTs}>{formatarDataHora(a.ts)}</span>
              <code className={styles.auditAction}>{a.action}</code>
              <span className={styles.muted}>
                {a.resource_type}/{a.resource_id}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
